import { observer } from 'mobx-react-lite'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import {
  ToggleListItem,
  useGlobalCopyStore,
  useScreenSize,
  useSettingsStore,
  useUserStore,
} from '../../../common'
import { DetailedNotificationProfile } from '../../../core'

import { AccountProfileListItem } from './AccountProfileListItem'

interface AccountNotificationProfileProps {
  onUpdateProfile: (profile: DetailedNotificationProfile) => void
  profile: DetailedNotificationProfile
}

export const AccountNotificationProfile = observer(
  ({ onUpdateProfile, profile }: AccountNotificationProfileProps) => {
    const { settings: copy } = useGlobalCopyStore()
    const { user } = useUserStore()
    const { clientCommunicationProfile, userCommunicationsProfile } = useSettingsStore()
    const { isMobile } = useScreenSize()

    const isAccountProfile = profile.notification_type > 2
    const {
      opted_out_of_email_notifications: emailDisabled,
      opted_out_of_push_notifications: pushDisabled,
      opted_out_of_sms_notifications: smsDisabled,
    } = userCommunicationsProfile

    const {
      email_notifications_enabled: showEmail,
      push_notifications_enabled: showPush,
      sms_notifications_enabled: showSms,
    } = clientCommunicationProfile

    return (
      <Stack>
        {(showEmail || showSms || showPush) && (
          <>
            <Text bold={true} sx={{ mb: 4, ml: 24, mt: 12 }} variant="caption">
              {copy.delivery_method_title}
            </Text>
            <List sx={{ bgcolor: 'background.paper' }}>
              {showEmail && (
                <Stack sx={{ pl: 24 }}>
                  <ToggleListItem
                    isChecked={!emailDisabled && profile.email_channel && !!user.email}
                    isDisabled={emailDisabled || !user.email}
                    onToggle={() =>
                      onUpdateProfile({ ...profile, email_channel: !profile.email_channel })
                    }
                    primaryText={copy.delivery_method_email_label}
                    secondaryText={
                      emailDisabled || !profile.email_channel || !user.email
                        ? copy.notification_disabled
                        : copy.notification_enabled
                    }
                    useExtraPadding={false}
                  />
                </Stack>
              )}

              {showEmail && (showSms || showPush) && <Divider sx={{ ml: isMobile ? 0 : 24 }} />}

              {showSms && (
                <Stack sx={{ pl: 24 }}>
                  <ToggleListItem
                    isChecked={!smsDisabled && profile.sms_channel && !!user.phone}
                    isDisabled={smsDisabled || !user.phone}
                    onToggle={() =>
                      onUpdateProfile({ ...profile, sms_channel: !profile.sms_channel })
                    }
                    primaryText={`${copy.delivery_method_sms_label}`}
                    secondaryText={
                      smsDisabled || !profile.sms_channel || !user.phone
                        ? copy.notification_disabled
                        : copy.notification_enabled
                    }
                    useExtraPadding={false}
                  />
                </Stack>
              )}

              {showSms && showPush && <Divider sx={{ ml: isMobile ? 0 : 24 }} />}

              {showPush && (
                <Stack sx={{ pl: 24 }}>
                  <ToggleListItem
                    isChecked={!pushDisabled && profile.push_channel}
                    isDisabled={pushDisabled}
                    onToggle={() =>
                      onUpdateProfile({ ...profile, push_channel: !profile.push_channel })
                    }
                    primaryText={copy.delivery_method_push_label}
                    secondaryText={copy.delivery_method_push_subtitle}
                    useExtraPadding={false}
                  />
                </Stack>
              )}
              <Divider />
            </List>
          </>
        )}

        {isAccountProfile && Boolean(profile.children?.length) && (
          <Stack>
            <Text bold={true} sx={{ mb: 4, ml: 24, mt: 12 }} variant="caption">
              {copy.accounts_threshold_title}
            </Text>

            <List sx={{ bgcolor: 'background.paper' }}>
              {profile.children?.map((child) => (
                <AccountProfileListItem
                  key={child.guid}
                  onProfileUpdated={onUpdateProfile}
                  profile={child}
                />
              ))}
            </List>
          </Stack>
        )}
      </Stack>
    )
  },
)
